import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { switchMap } from 'rxjs/operators';

import { FlexRow } from '@app/flex-framework/flex-api.model';
import { SectionDisplayInfo } from '@app/flex-framework/flex-section-api.model';
import { FlexService } from '@app/flex-framework/services/flex-framework.service';
import { FlexRowMenuService } from '@app/flex-framework/services/flex-row-menu.service';
import { FlexRowResourceService } from '@app/flex-framework/services/flex-row-resource.service';
import { FlexRowService } from '@app/flex-framework/services/flex-row.service';
import { LinkRowLink } from '@app/shared/components/link-row/link-row.model';
import {
  MenuViewModel,
  ModifyOptionsFnType,
} from '@app/shared/components/menu/menu.component';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { AuthService } from '@app/shared/services/auth.service';
import { Resource } from '@app/target/target-api.model';
import { of } from 'rxjs';

@Component({
  selector: 'dgx-flex-row',
  styleUrls: ['./flex-row.component.scss'],
  templateUrl: './flex-row.component.html',
})
export class FlexRowComponent
  extends SubscriberBaseDirective
  implements OnInit
{
  public isFeatured?: boolean;
  public isEditing: boolean;
  public isReordering: boolean = false;
  public menuConfig: MenuViewModel[];
  public displayInfo: SectionDisplayInfo;
  public disablePlanImage: boolean;

  @Input() public links: LinkRowLink[];
  @Input() public loadingSuggestions: boolean;
  @Input() public row: FlexRow;
  @Input() public permanentEditMode: boolean;
  @Input() public fetchTargetResource = true;
  @Input() public customSaveResources;
  @Input() public customSlidesPerView: number;
  @Input() public hideOldHeaderLinks: boolean;
  /** Use DgxCarousel instead of FlexGrid. @deprecated - Migrate to use FlexGrid ASAP. */
  @Input() public useCarousels = false;
  /**
   * Override the default ModifyMenuOptions, which is used to customize resources menu config.
   * Default ModifyMenuOptionsFn adds a "Remove from Plan" to the menu
   * see `menu.component.ts` for more information on using `ModifyOptionsFnType`
   */
  @Input() public customModifyMenuOptionsFn?: ModifyOptionsFnType<any>;

  @Output() public removeSuggestion = new EventEmitter<{
    row: FlexRow;
    item: Resource;
  }>();
  @Output() public removeRow = new EventEmitter<FlexRow>();
  @Output() public rowEdited = new EventEmitter<FlexRow>();

  constructor(
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private flexService: FlexService,
    private flexRowMenuService: FlexRowMenuService,
    private flexRowResourceService: FlexRowResourceService,
    private flexRowService: FlexRowService,
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    super();
  }

  public get hideLinks() {
    return this.hideOldHeaderLinks !== undefined
      ? this.hideOldHeaderLinks
      : !this.row.canViewAll;
  }

  public ngOnInit(): void {
    this.disablePlanImage =
      this.authService.authUser?.defaultOrgInfo?.settings.disablePlanImage;

    // No need to do any subscribe logic if we're in PEM.
    if (this.permanentEditMode) {
      this.isEditing = true;
      this.setupRow();
      this.updateRowView(false);
      return;
    }

    // TODO: Move this to the parent (e.g., flex-overview), so that we aren't listening for
    // queryParams on every flex-row; once that's done, move setupRow and updateRowView out
    // of this subscribe.
    this.activatedRoute.queryParams
      ?.pipe(this.takeUntilDestroyed())
      .subscribe(({ editMode }) => {
        this.isEditing = editMode === 'true';
        this.setupRow();
        this.updateRowView(false);
      });
  }

  public addResource() {
    this.flexRowResourceService
      .addResource(this.row, this.customSaveResources)
      .pipe(
        switchMap(() =>
          this.fetchTargetResource
            ? this.flexRowService.getRowResources(
                this.row,
                true,
                this.isEditing
              )
            : of([])
        )
      )
      .subscribe((resources: Resource[]) => {
        if (resources.length) {
          this.flexRowResourceService.setTargetOrgIdFromResource(resources);
        }
        this.row = {
          ...this.flexRowService.updateRowLayout(
            this.row,
            this.isEditing,
            this.useCarousels
          ),
          loading: false,
          targetResources: [...resources],
        };

        this.flexRowService.rowUpdate();
        this.configureRowResourceMenus();

        this.updateRowView();
      });
  }

  public onRemoveSuggestion(event) {
    this.removeSuggestion.emit(event);
  }

  public updateRow(referenceId: number) {
    this.row = {
      ...this.row,
      targetResources: this.row.targetResources.filter(
        (resource) => resource.referenceId !== referenceId
      ),
      // For rows displaying all their content at once, i.e. where layoutConfig is 'All',
      // the section.resourceCount property will be *undefined*.
      section: {
        ...this.row.section,
        resourceCount:
          this.row.section.layoutConfig.displayType !== 'All'
            ? this.row.section.resourceCount !== undefined
              ? this.row.section.resourceCount - 1
              : // Take into account also that we might have switched from 'All' to compact,
                // and avoid doing math with a value that was previously undefined.
                this.row.targetResources?.length ?? 0 - 1
            : undefined,
      },
    };
    this.flexRowService.rowUpdate();
    this.updateRowView();
  }

  public configureRowResourceMenus() {
    this.row.updateRowFn = (referenceId: number) => this.updateRow(referenceId);
    this.row.targetResources?.forEach((resource) => {
      this.row = {
        ...this.row,
        menuConfig: this.flexRowResourceService.configureRowResourceMenu(
          this.row,
          resource,
          this.isEditing
        ),
        modifyMenuOptionsFn:
          this.customModifyMenuOptionsFn ??
          this.flexRowResourceService.getModifyMenuConfigFn(this.row),
      };
    });
  }

  public onRemoveRow() {
    this.removeRow.emit(this.row);
    this.cdr.detectChanges();
  }

  public onEditRow(updatedRow: FlexRow) {
    // apply updates from our child components to our row.
    this.row = { ...this.row, ...updatedRow };
    // set up the menu again so that the updated row is passed to our edit modal.
    this.updateMenuConfig();
    // inform the overview component that the row has been updated?
    this.flexRowService.rowUpdate();
    // update the row view.
    // TODO: We can probably consolidate some of this logic.
    this.updateRowView();
  }

  /** Purely cosmetic updates to show our compact-view content. */
  public onShowUpdate(isViewAll = false) {
    this.row.isViewAll = isViewAll;
    this.updateRowView(false);
  }

  public onToggleReorder() {
    this.isReordering = !this.isReordering;
    this.onShowUpdate(!this.row.isViewAll);
  }

  private setupLinks() {
    this.links =
      this.links ||
      this.flexRowMenuService.getRowLinksConfig(
        this.row,
        this.isEditing,
        this.router.url
      );
  }

  private setupRow() {
    this.displayInfo = this.flexService.getRowDisplayInfo(
      this.row.section.resourceType
    );

    this.row.section.name =
      this.row.section.name ||
      (this.displayInfo?.isRequiredTitle ? this.row.displayType : '');

    this.updateMenuConfig();
    this.setupLinks();
    this.configureRowResourceMenus();
  }

  private updateMenuConfig() {
    this.menuConfig = this.flexRowMenuService.getFlexRowMenu(
      this.row,
      this.activatedRoute,
      (updatedRow: FlexRow) => this.onEditRow(updatedRow),
      () => this.onRemoveRow()
    );
  }

  private updateRowView(emitUpdates = true) {
    this.row = {
      ...this.flexRowService.updateRowLayout(this.row, this.isEditing),
    };
    this.cdr.markForCheck();
    this.cdr.detectChanges();
    if (emitUpdates) {
      this.rowEdited.emit(this.row);
    }
  }
}
